/**
 * Public subscription plans for XpansVault.
 * Enforced per organization (tenant), with per-user storage allotments inside each org.
 * Keep capability maps aligned with Backend: services/subscription/plans.js
 */

const starterCapabilities = {
  adminDigest: true,
  teams: false,
  storageRequests: false,
  activityHistory: false,
  orgAuditLogs: false,
  advancedSearch: false,
  tags: false,
  watermark: false,
  ocr: false,
  multiOrganization: false,
  webhooks: false,
  ipAllowlists: false,
  retention: false,
  legalHold: false,
  quarantine: false,
  gdprExport: false,
  sessionsAdmin: false
}

const businessCapabilities = {
  ...starterCapabilities,
  teams: true,
  storageRequests: true,
  activityHistory: true,
  orgAuditLogs: true,
  advancedSearch: true,
  tags: true,
  watermark: true,
  ocr: true
}

const enterpriseCapabilities = {
  ...businessCapabilities,
  multiOrganization: true,
  webhooks: true,
  ipAllowlists: true,
  retention: true,
  legalHold: true,
  quarantine: true,
  gdprExport: true,
  sessionsAdmin: true
}

export const SUBSCRIPTION_PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    price: '₹999',
    priceINR: 999,
    period: '/ month',
    blurb: 'For a single organization getting documents out of email and shared drives.',
    maxUsers: 10,
    storageGB: 100,
    storage: '100 GB shared storage',
    seats: 'Up to 10 users',
    cta: 'Start with Starter',
    ctaTo: '/register',
    featured: false,
    capabilities: starterCapabilities,
    features: [
      'My Drive, Shared with Me, Shared by Me',
      'Folders, preview, and download controls',
      'Editor / Viewer sharing with live role updates',
      'Company admin user invites & approvals',
      'Organization analytics & storage visibility',
      'In-app help documentation',
      'Trash with soft delete',
      'Email support'
    ]
  },
  {
    id: 'business',
    name: 'Business',
    price: '₹2,999',
    priceINR: 2999,
    period: '/ month',
    blurb: 'For growing organizations that need collaboration, storage headroom, and admin control.',
    maxUsers: 50,
    storageGB: 1024,
    storage: '1 TB shared storage',
    seats: 'Up to 50 users',
    cta: 'Choose Business',
    ctaTo: '/register',
    featured: true,
    capabilities: businessCapabilities,
    features: [
      'Everything in Starter',
      'Teams and bulk sharing',
      'Storage increase requests & plan-capped allotments',
      'Activity history and notifications',
      'Org-scoped audit logs for company admins',
      'Advanced search, tags, and OCR search',
      'Watermark downloads',
      'Document approval workflows',
      'Priority email support'
    ]
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 'Custom',
    priceINR: null,
    period: '',
    blurb: 'For platforms that run many organizations with governance and tailored capacity. From ₹12,999 / month.',
    maxUsers: null,
    storageGB: null,
    storage: 'Custom storage & seats',
    seats: 'Unlimited users',
    cta: 'Talk to sales',
    ctaTo: '/contact',
    featured: false,
    capabilities: enterpriseCapabilities,
    features: [
      'Everything in Business',
      'Multi-organization workspaces',
      'Platform Admin controls for plans & tenants',
      'SSO (OIDC), admin MFA, and session controls',
      'Retention, legal hold, GDPR export',
      'IP allowlists, malware quarantine, webhooks',
      'SIEM NDJSON audit export',
      'Dedicated onboarding'
    ]
  }
]

export const getPlanById = (planId) =>
  SUBSCRIPTION_PLANS.find((plan) => plan.id === planId) ||
  SUBSCRIPTION_PLANS.find((plan) => plan.id === 'starter')

export const getPlanCapabilities = (planId) => {
  const plan = getPlanById(planId)
  return { ...starterCapabilities, ...(plan.capabilities || {}) }
}

export const planHasCapability = (planId, capability) => Boolean(getPlanCapabilities(planId)[capability])

export default SUBSCRIPTION_PLANS
